import { useCallback, useState } from 'react';
import { EditChannelDialog, type EditChannelDialogProps } from '@/components/EditChannelDialog';
import { loadPanelUser } from '@/lib/api/panel.api';
import { useI18nText } from '@/lib/utils/i18nText';
import { useAppDispatch } from '@/store/hooks';
import { showSnackbar } from '@/store/slices/snackbarSlice';

/**
 * "Edit Channel" affordance shared by every place that offers it from a channel
 * row/rail: the dialog needs the *full* panel user, which the list pages don't
 * have, so opening it is always "fetch by id, then show". Both
 * `ChannelNameQuickButtons` and the merged channel drawer's rail
 * (`ChannelDrawerRail`) drive it, so the fetch + error snackbar + dialog live
 * here once instead of being copied per call site.
 *
 * Render `dialog` somewhere in the caller's tree; call `openFor(userId)` from the
 * trigger and use `loading` to disable/spin it while the user loads.
 */
export function useChannelEditDialog() {
  const { t } = useI18nText();
  const dispatch = useAppDispatch();
  const [editUser, setEditUser] = useState<EditChannelDialogProps['user'] | null>(null);
  const [loading, setLoading] = useState(false);

  const openFor = useCallback(async (userId: string) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await loadPanelUser<{ user: EditChannelDialogProps['user'] }>(userId);
      setEditUser(res.user);
    } catch {
      dispatch(showSnackbar({
        text: t('Failed to load channel details', '채널 정보 로드 실패'),
        kind: 'api-error',
        timestamp: Date.now(),
      }));
    } finally {
      setLoading(false);
    }
  }, [loading, dispatch, t]);

  const dialog = editUser
    ? <EditChannelDialog open user={editUser} onClose={() => setEditUser(null)} />
    : null;

  return { openFor, loading, dialog };
}
